import { useEffect, useRef, useState } from "react";
import type { WebShopPublicUser } from "@/api_client";
import { useContent } from "@/hooks/api/content/useContent";
import { handleEntryProductPage } from "@/web_analytics/dataLayer/pageLoaded/pageLoadedEmitters";
import { useSetCustomerContext } from "../../hooks/api/customer_context/useSetCustomerContext";
import { useOffer } from "../../hooks/api/offer/useOffer";
import { useAddProduct } from "../../hooks/api/product/useAddProduct";
import { useUser } from "../../hooks/api/useUser";
import { findQuote } from "../../utils/productUtils";

// Inline type references (avoid top-level type-only imports per lint rules)
type Channel = import("@/api_client").Channel;
type ProductType = import("@/api_client").ProductType;

type UseEntryCommonParams = {
  productType: ProductType; // e.g. 'travel' | 'householder'
  backendHost?: string;
  appPathPrefix?: string;
  skipCart: boolean;
  channel?: Channel;
  commonContentPageKey?: string; // e.g 'householderCommonPage'
  language?: string;
};

// Inline client type references
type Quote = import("@/api_client").Quote;
type AddQuoteResult = import("@/api_client").AddQuoteResult;
type PolicyHolder = import("@/api_client").PolicyHolder | null | undefined;
type AddProductOffer = AddQuoteResult["offer"];

interface UseEntryCommonReturn<TCommonContent> {
  isLoading: boolean;
  isSubmitting: boolean;
  submitEntryHandler: (quote: Quote) => Promise<AddQuoteResult>;
  policyHolder: PolicyHolder;
  user: WebShopPublicUser | undefined;
  commonContent?: TCommonContent | null;
}

async function awaitCustomerContextBeforeSubmit(
  customerContextPromise: Promise<void> | null,
) {
  if (customerContextPromise == null) {
    return;
  }

  try {
    await customerContextPromise;
  } catch (error) {
    console.error(
      "Error setting customer context before submitting entry",
      error,
    );
    globalThis.appInsights?.trackException({
      exception: error instanceof Error ? error : new Error(String(error)),
    });
  }
}

function getSubmitRoute(offer: AddProductOffer, quoteId: AddQuoteResult["id"]) {
  const foundQuote = findQuote(offer, quoteId);

  if (foundQuote?.priceCalculatedAt == null) {
    return "edit";
  }

  return "price";
}

function normalizeBackendHost(backendHost: string) {
  return backendHost.replaceAll(/\/+$/gu, "");
}

function normalizeAppPathPrefix(appPathPrefix?: string) {
  return appPathPrefix?.replaceAll(/(^\/+)|(\/+$)/gu, "") ?? "";
}

function getRedirectUrl({
  added,
  appPathPrefix,
  backendHost,
  productType,
}: {
  added: AddQuoteResult;
  appPathPrefix?: string;
  backendHost: string;
  productType: ProductType;
}) {
  const route = getSubmitRoute(added.offer, added.id);
  const normalizedBackendHost = normalizeBackendHost(backendHost);
  const normalizedAppPathPrefix = normalizeAppPathPrefix(appPathPrefix);
  const redirectPathPrefix = normalizedAppPathPrefix
    ? `/${normalizedAppPathPrefix}`
    : "";

  return `${normalizedBackendHost}${redirectPathPrefix}/${productType}/${route}?id=${added.id}&itm_ws_source=prpg`;
}

/**
 * Shared logic for product entry web components.
 * Handles loading state aggregation and submit flow.
 */
export function useEntryForWebComponents<TCommonContent = null>({
  productType,
  backendHost,
  appPathPrefix,
  skipCart,
  channel,
  language,
  commonContentPageKey,
}: UseEntryCommonParams): UseEntryCommonReturn<TCommonContent> {
  const { data: offer, isLoading: isLoadingOffer } = useOffer();
  const { mutateAsync: setCustomerContext } = useSetCustomerContext();

  const shouldFetchSiteContent = Boolean(commonContentPageKey);
  const { data: siteContent } = useContent(shouldFetchSiteContent, language);
  const { data: user, isLoading: isLoadingUser } = useUser();

  const {
    mutateAsync: addProduct,
    isPending: addProductPending,
    isSuccess: addProductSuccess,
  } = useAddProduct();

  // Track the in-flight customer context mutation so we can await it before submit
  const customerContextPromiseRef = useRef<Promise<void> | null>(null);
  const [isSubmittingChain, setIsSubmittingChain] = useState(false);

  // fire customer context set as soon as possible, we have the payload data from the start
  // this is so that by the time user submits, the context is already set and we don't add extra wait time to submit
  useEffect(() => {
    async function setCustomerContextAndPageLoadAsync() {
      const payload = { skipCart, channel, language: language ?? "fi" };
      globalThis.appInsights?.trackEvent({
        name: "customercontext.setting",
        properties: { productType, ...payload },
      });
      const customerContext = await setCustomerContext({ body: payload });
      globalThis.appInsights?.trackEvent({
        name: "customercontext.set",
        properties: {
          productType,
          ...payload,
          contextId: customerContext.publicId,
        },
      });
      // Emit page loaded event for entry product page needs the customer context
      handleEntryProductPage({
        productType,
        channel: channel ?? "WS",
        customerContext: customerContext || {},
        contextId: customerContext?.publicId ?? "",
      });
    }
    // store the promise so we can await it in submit so we are sure it's done before submit
    customerContextPromiseRef.current = setCustomerContextAndPageLoadAsync();
  }, [setCustomerContext, skipCart, channel, language, productType]);

  const isLoading = isLoadingOffer || isLoadingUser;
  const isSubmitting =
    isSubmittingChain || addProductPending || addProductSuccess;

  const submitEntryHandler = async (quote: Quote): Promise<AddQuoteResult> => {
    setIsSubmittingChain(true);
    try {
      // Ensure customer context is set before adding the product.
      // We start it in useEffect above to avoid adding that latency on submit.
      await awaitCustomerContextBeforeSubmit(customerContextPromiseRef.current);

      const added = await addProduct(quote);
      if (backendHost) {
        globalThis.location.href = getRedirectUrl({
          added,
          appPathPrefix,
          backendHost,
          productType,
        });
      }

      return added;
    } finally {
      setIsSubmittingChain(false);
    }
  };

  let commonContent: TCommonContent | undefined;
  if (commonContentPageKey) {
    const rawSiteContent = siteContent as unknown as Record<string, unknown>;
    commonContent = rawSiteContent?.[commonContentPageKey] as TCommonContent;
  }

  return {
    isLoading,
    isSubmitting,
    submitEntryHandler,
    policyHolder: offer?.policyHolder,
    user,
    commonContent,
  };
}
